/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ActionGroup, ActionKernelType } from "client-code/actions";
import { ActionsService, RecordingsService } from "@tivocorp/data-domain-lib";
import type {
  TActionsParams,
  TCustomActionList,
  TRecordingsDeleteRecordingIdsParams,
  TRecordingsHealthParams,
  TRecordingsInfoParams,
  TRecordingsOfferMutationParams,
  TRecordingsOfferScheduleParams,
  TRecordingsPagedParams,
  TRecordingsRecordingIdentifierParams,
  TRecordingsRecordingMutationParams,
  TRecordingsRecordingScheduleParams,
  TRecordingsUpdateLastViewedPositionParams
} from "@tivocorp/data-domain-lib";
import type {
  HealthDetails,
  HistoryItem,
  LastViewedPosition,
  RecordingOptions,
  RecordingValues,
  RecordingValuesWithDisplayOptions,
  ToDoItem
} from "client-code/cloudcore-recordings";

import { ApplicationFeatureArea } from "../../enums";
import { BaseInteractor, ServiceLocator } from "../base";
import type { IRecordingsServiceInteractor } from "../../interfaces";
import { AnalyticsLogger } from "../../logging";
import { Log } from "../../logging/Log";
import { TIVO_SERVICES } from "../../../config";
import type { TAnalyticsFields, TNetworkOptions } from "../../types";
import { ActionsInteractor } from "./ActionsInteractor";
import { DeviceFeatures } from "../../device";
import { PostSigninInteractor } from "../postSigninInteractor";
import { clearServiceCache } from "./serviceCache";

export type TCreateOfferRecordingParams = {
  offerId: string;
  contentId?: string;
  channelNumber?: string;
  channelId?: string;
  /**
   * Scheduled start of the airing being recorded, used for the fallback
   * recordingValues when GET /schedule gives none. Callers supply it because
   * only they know which airing is meant — the player reads it off playback
   * metadata, a detail screen off the offer it is showing. Defaults to now.
   */
  airingStartTime?: string;
  recordingValues?: Record<string, unknown>;
};

type TRecordingValues = Record<string, unknown>;

type TRecordingValuesWithDisplayOptions = {
  recordingValues?: TRecordingValues;
  alwaysShowOverlay?: boolean;
};

/**
 * Feature interactor for Recordings domain operations.
 *
 * Extends BaseInteractor so service-param construction (accessToken via
 * TokenManager, bodyId, TSRI endpoint resolution) is handled uniformly.
 * Implements {@link IRecordingsServiceInteractor}.
 */
export class RecordingsServiceInteractor
  extends BaseInteractor
  implements IRecordingsServiceInteractor
{
  private readonly TAG: string = "RecordingsServiceInteractor";

  /**
   * @param mFeatureArea - ApplicationFeatureArea reported in the
   *                       request header for every call made by this
   *                       instance. Defaults to {@link ApplicationFeatureArea.ALL}.
   */
  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  /**
   * Builds fallback recordingValues when GET /schedule fails or is missing
   * values. We use the airing start time so recording aligns to program start.
   */
  private static buildDefaultRecordingValues(
    offerId: string,
    channelId: string | undefined,
    dateTime: string
  ): TRecordingValues {
    return {
      keepUntil: "spaceNeeded",
      startRecordingEarly: { type: "StartRecordingOnTime" },
      stopRecordingLate: { type: "StopRecordingOnTime" },
      timeAndChannel: { offerId, channelId, dateTime }
    };
  }

  private async createRecordingsService(): Promise<RecordingsService> {
    return ServiceLocator.getInstance(
      RecordingsService,
      await this.buildServicesParams(
        TIVO_SERVICES.RecordingsService,
        this.mFeatureArea
      )
    );
  }

  /** Resolves a recordable content target without creating a recording. */
  public async resolveOfferRecordingContentId(
    offerId: string,
    contentId?: string,
    options?: TNetworkOptions
  ): Promise<string> {
    if (contentId !== undefined && contentId.trim() !== "") {
      return contentId;
    }
    const response = await new ActionsInteractor(
      ApplicationFeatureArea.ALL
    ).fetchOfferActions(
      { offerId, groups: [ActionGroup.ScheduleRecording] },
      false,
      options
    );
    const kernels: ReadonlySet<string> = new Set([
      ActionKernelType.RecordThisShowActionKernel,
      ActionKernelType.RecordThisMovieActionKernel,
      ActionKernelType.RecordThisEpisodeActionKernel,
      ActionKernelType.RecordThisGameActionKernel,
      ActionKernelType.RecordThisMatchActionKernel,
      ActionKernelType.RecordThisRaceActionKernel,
      ActionKernelType.RecordThisEventActionKernel
    ]);
    for (const action of response.actions) {
      if (action.kernel === null || typeof action.kernel !== "object") {
        continue;
      }
      const kernel: Record<string, unknown> = { ...action.kernel };
      if (
        typeof kernel.actionKernelType === "string" &&
        kernels.has(kernel.actionKernelType) &&
        typeof kernel.contentId === "string" &&
        kernel.contentId.trim() !== ""
      ) {
        return kernel.contentId;
      }
    }
    throw new Error("contentId is required for recording");
  }

  public async fetchHealth(
    params: TRecordingsHealthParams,
    options?: TNetworkOptions
  ): Promise<HealthDetails> {
    return this.executeGuarded(
      "fetchHealth",
      async (guardedSignal: AbortSignal): Promise<HealthDetails> => {
        Log.d(this.TAG, "fetchHealth() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchHealth(params, guardedSignal);
      },
      options
    );
  }

  public async fetchInfo(
    params: TRecordingsInfoParams,
    options?: TNetworkOptions
  ): Promise<object> {
    return this.executeGuarded(
      "fetchInfo",
      async (guardedSignal: AbortSignal): Promise<object> => {
        Log.d(this.TAG, "fetchInfo() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchInfo(params, guardedSignal);
      },
      options
    );
  }

  public async fetchLastViewedPosition(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions
  ): Promise<LastViewedPosition> {
    return this.executeGuarded(
      "fetchLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<LastViewedPosition> => {
        Log.d(this.TAG, "fetchLastViewedPosition() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchLastViewedPosition(params, guardedSignal);
      },
      options
    );
  }

  public async updateLastViewedPosition(
    params: TRecordingsUpdateLastViewedPositionParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "updateLastViewedPosition",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "updateLastViewedPosition() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.updateLastViewedPosition(params, guardedSignal);
      },
      options
    );
  }

  public async fetchRecordingsHistoryList(
    params: TRecordingsPagedParams,
    options?: TNetworkOptions
  ): Promise<HistoryItem[]> {
    return this.executeGuarded(
      "fetchRecordingsHistoryList",
      async (guardedSignal: AbortSignal): Promise<HistoryItem[]> => {
        Log.d(this.TAG, "fetchRecordingsHistoryList() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchRecordingsHistoryList(params, guardedSignal);
      },
      options
    );
  }

  public async fetchRecordingsHistoryListV2(
    params: TRecordingsPagedParams,
    options?: TNetworkOptions
  ): Promise<HistoryItem[]> {
    return this.executeGuarded(
      "fetchRecordingsHistoryListV2",
      async (guardedSignal: AbortSignal): Promise<HistoryItem[]> => {
        Log.d(this.TAG, "fetchRecordingsHistoryListV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchRecordingsHistoryListV2(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Deletes completed recordings and fires the `deleteRecording` analytics
   * event on success — instrumented here rather than at each call site so
   * every screen that deletes recordings through this interactor reports it
   * identically. `is_soft_delete` is sent `"false"`: v1 has no undo.
   */
  public async deleteRecordingsByRecordingIds(
    params: TRecordingsDeleteRecordingIdsParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteRecordingsByRecordingIds",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteRecordingsByRecordingIds() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteRecordingsByRecordingIds(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteRecordingsByRecordingIds() succeeded count=${params.requestBody.length}`
        );
        this.logDeleteRecording(params.requestBody, "false", analyticsFields);
      },
      options
    );
  }

  /**
   * Deletes completed recordings (recoverable — moves to the Deleted tab) and
   * fires the `deleteRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async deleteRecordingsByRecordingIdsV2(
    params: TRecordingsDeleteRecordingIdsParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteRecordingsByRecordingIdsV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteRecordingsByRecordingIdsV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteRecordingsByRecordingIdsV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteRecordingsByRecordingIdsV2() succeeded count=${params.requestBody.length}`
        );
        this.logDeleteRecording(params.requestBody, "true", analyticsFields);
      },
      options
    );
  }

  /** Shared `deleteRecording` emission for the v1/v2 bulk-delete methods above. */
  private logDeleteRecording(
    recordingIds: string[],
    isSoftDelete: "true" | "false",
    analyticsFields?: Partial<TAnalyticsFields>
  ): void {
    AnalyticsLogger.logDeleteRecording({
      recording_id: recordingIds.join(","),
      is_soft_delete: isSoftDelete,
      batch_id:
        recordingIds.length > 1 ? String(recordingIds.length) : undefined,
      ...analyticsFields
    });
  }

  public async fetchOfferRecordingOptions(
    params: TRecordingsOfferScheduleParams,
    options?: TNetworkOptions
  ): Promise<RecordingOptions> {
    return this.executeGuarded(
      "fetchOfferRecordingOptions",
      async (guardedSignal: AbortSignal): Promise<RecordingOptions> => {
        Log.d(this.TAG, "fetchOfferRecordingOptions() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchOfferRecordingOptions(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Cancels a scheduled single recording before it airs and fires the
   * `cancelRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async deleteOfferRecordingSchedule(
    params: TRecordingsOfferScheduleParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteOfferRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteOfferRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteOfferRecordingSchedule(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteOfferRecordingSchedule() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logCancelRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  public async fetchOfferRecordingSchedule(
    params: TRecordingsOfferScheduleParams,
    options?: TNetworkOptions
  ): Promise<RecordingValuesWithDisplayOptions> {
    return this.executeGuarded(
      "fetchOfferRecordingSchedule",
      async (
        guardedSignal: AbortSignal
      ): Promise<RecordingValuesWithDisplayOptions> => {
        Log.d(this.TAG, "fetchOfferRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchOfferRecordingSchedule(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Schedules a single recording (offer-keyed POST) and fires the
   * `createRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async createOfferRecordingSchedule(
    params: TRecordingsOfferMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "createOfferRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "createOfferRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.createOfferRecordingSchedule(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `createOfferRecordingSchedule() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logCreateRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  /**
   * Modifies a scheduled single recording (offer-keyed PUT) and fires the
   * `modifyRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async updateOfferRecordingSchedule(
    params: TRecordingsOfferMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "updateOfferRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "updateOfferRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.updateOfferRecordingSchedule(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `updateOfferRecordingSchedule() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logModifyRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  /** Cancels a scheduled single recording (v2) — see {@link deleteOfferRecordingSchedule}. */
  public async deleteOfferRecordingScheduleV2(
    params: TRecordingsOfferScheduleParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteOfferRecordingScheduleV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteOfferRecordingScheduleV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteOfferRecordingScheduleV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteOfferRecordingScheduleV2() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logCancelRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  /** Schedules a single recording (v2) — see {@link createOfferRecordingSchedule}. */
  public async createOfferRecordingScheduleV2(
    params: TRecordingsOfferMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "createOfferRecordingScheduleV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "createOfferRecordingScheduleV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.createOfferRecordingScheduleV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `createOfferRecordingScheduleV2() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logCreateRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  /** Modifies a scheduled single recording (v2) — see {@link updateOfferRecordingSchedule}. */
  public async updateOfferRecordingScheduleV2(
    params: TRecordingsOfferMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "updateOfferRecordingScheduleV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "updateOfferRecordingScheduleV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.updateOfferRecordingScheduleV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `updateOfferRecordingScheduleV2() succeeded offerId=${params.offerId}`
        );
        AnalyticsLogger.logModifyRecording({
          content_id: params.contentId,
          channel_id: params.channelId,
          channel_num: params.channelNumber,
          ...analyticsFields
        });
      },
      options
    );
  }

  public async fetchRecordingOptions(
    params: TRecordingsRecordingScheduleParams,
    options?: TNetworkOptions
  ): Promise<RecordingOptions> {
    return this.executeGuarded(
      "fetchRecordingOptions",
      async (guardedSignal: AbortSignal): Promise<RecordingOptions> => {
        Log.d(this.TAG, "fetchRecordingOptions() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchRecordingOptions(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Restores a deleted recording and fires the `modifyRecording` analytics
   * event on success — a restore is a state change on the recording, not a
   * fresh create. See {@link deleteRecordingsByRecordingIds}.
   */
  public async recoverRecording(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "recoverRecording",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "recoverRecording() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.recoverRecording(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `recoverRecording() succeeded recordingId=${params.recordingId}`
        );
        AnalyticsLogger.logModifyRecording({
          recording_id: params.recordingId,
          reason: "undelete",
          ...analyticsFields
        });
      },
      options
    );
  }

  /**
   * Permanently deletes one already-deleted recording and fires the
   * `deleteRecording` analytics event on success.
   *
   * The counterpart to {@link recoverRecording} on the Deleted tab: that one
   * undoes the deletion, this one makes it final. `is_soft_delete` is sent
   * `"false"` — there is no undo behind this endpoint, the same way
   * {@link deleteRecordingsByRecordingIds} reports v1's hard delete.
   */
  public async permanentlyDeleteRecording(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "permanentlyDeleteRecording",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "permanentlyDeleteRecording() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.permanentlyDeleteRecording(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `permanentlyDeleteRecording() succeeded recordingId=${params.recordingId}`
        );
        this.logDeleteRecording([params.recordingId], "false", analyticsFields);
      },
      options
    );
  }

  public async saveRecording(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "saveRecording",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "saveRecording() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.saveRecording(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
      },
      options
    );
  }

  public async unsaveRecording(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions
  ): Promise<void> {
    return this.executeGuarded(
      "unsaveRecording",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "unsaveRecording() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.unsaveRecording(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
      },
      options
    );
  }

  /**
   * Cancels a scheduled single recording (by recordingId) and fires the
   * `cancelRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async deleteRecordingSchedule(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteRecordingSchedule(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteRecordingSchedule() succeeded recordingId=${params.recordingId}`
        );
        AnalyticsLogger.logCancelRecording({
          recording_id: params.recordingId,
          ...analyticsFields
        });
      },
      options
    );
  }

  public async fetchRecordingSchedule(
    params: TRecordingsRecordingScheduleParams,
    options?: TNetworkOptions
  ): Promise<RecordingValues> {
    return this.executeGuarded(
      "fetchRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<RecordingValues> => {
        Log.d(this.TAG, "fetchRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchRecordingSchedule(params, guardedSignal);
      },
      options
    );
  }

  /**
   * Modifies a scheduled single recording (by recordingId) and fires the
   * `modifyRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async updateRecordingSchedule(
    params: TRecordingsRecordingMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "updateRecordingSchedule",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "updateRecordingSchedule() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.updateRecordingSchedule(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `updateRecordingSchedule() succeeded recordingId=${params.recordingId}`
        );
        AnalyticsLogger.logModifyRecording({
          recording_id: params.recordingId,
          content_id: params.contentId,
          ...analyticsFields
        });
      },
      options
    );
  }

  /**
   * Stops an in-progress recording (by recordingId), keeping what was already
   * recorded, and fires the `cancelRecording` analytics event on success. See
   * {@link deleteRecordingsByRecordingIds}.
   */
  public async deleteRecordingScheduleV2(
    params: TRecordingsRecordingIdentifierParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "deleteRecordingScheduleV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "deleteRecordingScheduleV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.deleteRecordingScheduleV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `deleteRecordingScheduleV2() succeeded recordingId=${params.recordingId}`
        );
        AnalyticsLogger.logCancelRecording({
          recording_id: params.recordingId,
          ...analyticsFields
        });
      },
      options
    );
  }

  /** Modifies a scheduled single recording (v2) — see {@link updateRecordingSchedule}. */
  public async updateRecordingScheduleV2(
    params: TRecordingsRecordingMutationParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "updateRecordingScheduleV2",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(this.TAG, "updateRecordingScheduleV2() called");
        const service: RecordingsService = await this.createRecordingsService();
        await service.updateRecordingScheduleV2(params, guardedSignal);
        clearServiceCache(ActionsService, this.TAG);
        Log.i(
          this.TAG,
          `updateRecordingScheduleV2() succeeded recordingId=${params.recordingId}`
        );
        AnalyticsLogger.logModifyRecording({
          recording_id: params.recordingId,
          content_id: params.contentId,
          ...analyticsFields
        });
      },
      options
    );
  }

  public async fetchToDoList(
    params: TRecordingsPagedParams,
    options?: TNetworkOptions
  ): Promise<ToDoItem[]> {
    return this.executeGuarded(
      "fetchToDoList",
      async (guardedSignal: AbortSignal): Promise<ToDoItem[]> => {
        Log.d(this.TAG, "fetchToDoList() called");
        const service: RecordingsService = await this.createRecordingsService();
        return service.fetchToDoList(params, guardedSignal);
      },
      options
    );
  }

  public async createOfferRecording(
    {
      offerId,
      contentId,
      channelNumber,
      channelId,
      airingStartTime,
      recordingValues: selectedRecordingValues
    }: TCreateOfferRecordingParams,
    options?: TNetworkOptions,
    analyticsFields?: Partial<TAnalyticsFields>
  ): Promise<void> {
    return this.executeGuarded(
      "createOfferRecording",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(
          this.TAG,
          "createOfferRecording() called with input: " +
            JSON.stringify({
              offerId,
              contentId,
              channelNumber,
              channelId
            })
        );

        const isNpvrRecordingAllowed: boolean =
          await DeviceFeatures.getNpvrAllowedRecording(
            new PostSigninInteractor()
          );
        if (!isNpvrRecordingAllowed) {
          Log.w(
            this.TAG,
            "createOfferRecording() blocked by npvrAllowRecording=false"
          );
          throw new Error("NPVR recording is disabled");
        }

        // Ids come from the caller, which is the only layer that knows which
        // programme is meant: the player reads them off its playable and
        // playback metadata, a detail screen off the action kernel it rendered.
        // Resolving them here against the *playing* programme would schedule
        // the wrong recording for every non-player entry point.
        if (!offerId) {
          Log.w(this.TAG, "offerId is required for recording");
          throw new Error("offerId is required for recording");
        }
        const resolvedOfferId: string = offerId;

        // Resolve contentId: must be a content identifier (tivo:ct...), not offerId.
        let resolvedContentId: string | null =
          contentId !== undefined && contentId.trim() !== "" ? contentId : null;
        if (resolvedContentId === null) {
          Log.w(this.TAG, "contentId missing/empty from input");
          Log.d(
            this.TAG,
            "Trying actions-service fallback to resolve contentId from offerId"
          );

          const actionsParams: TActionsParams = {
            offerId: resolvedOfferId,
            groups: [
              ActionGroup.Watch,
              ActionGroup.ScheduleRecording,
              ActionGroup.ScheduleBookmark,
              ActionGroup.ScheduleOnepass,
              ActionGroup.Rent
            ]
          };

          const actionList: TCustomActionList = await new ActionsInteractor(
            ApplicationFeatureArea.ALL
          ).fetchOfferActions(actionsParams, false);

          // Only extract contentId from RecordThis* kernels; watch/bookmark/rent
          // kernels in the same action list do not carry recording identifiers.
          const RECORD_KERNEL_TYPES: ReadonlySet<string> = new Set([
            ActionKernelType.RecordThisShowActionKernel,
            ActionKernelType.RecordThisMovieActionKernel,
            ActionKernelType.RecordThisEpisodeActionKernel,
            ActionKernelType.RecordThisGameActionKernel,
            ActionKernelType.RecordThisMatchActionKernel,
            ActionKernelType.RecordThisRaceActionKernel,
            ActionKernelType.RecordThisEventActionKernel
          ]);
          const actionContentId: string | undefined = actionList.actions
            .map(
              (action) =>
                action.kernel as unknown as Record<string, unknown> | undefined
            )
            .filter(
              (kernel): kernel is Record<string, unknown> =>
                kernel !== undefined &&
                RECORD_KERNEL_TYPES.has(kernel.actionKernelType as string)
            )
            .map((kernel) => kernel.contentId)
            .find(
              (candidate): candidate is string =>
                typeof candidate === "string" && candidate.trim() !== ""
            );

          if (actionContentId !== undefined) {
            resolvedContentId = actionContentId;
            Log.d(
              this.TAG,
              "Using contentId from actions-service fallback: " +
                resolvedContentId
            );
          } else {
            Log.w(
              this.TAG,
              "Aborting recordings API call before network: missing required contentId"
            );
            throw new Error("contentId is required for recording");
          }
        }

        // Pinned to GUIDE rather than this.mFeatureArea, which every other
        // method here honours: that is the feature area this call has always
        // reported, and both callers reach it from a record affordance. Changing
        // it would silently move backend telemetry for scheduled recordings.
        const servicesParams = await this.buildServicesParams(
          TIVO_SERVICES.RecordingsService,
          ApplicationFeatureArea.GUIDE
        );
        const service: RecordingsService = ServiceLocator.getInstance(
          RecordingsService,
          servicesParams
        );
        // dateTime: the airing's scheduled start, so the recording aligns to
        // programme start rather than to when the button was pressed.
        const resolvedAiringStartTime: string =
          airingStartTime === undefined || airingStartTime === ""
            ? new Date().toISOString()
            : airingStartTime;
        Log.d(
          this.TAG,
          "createOfferRecording() resolved: offerId=" +
            resolvedOfferId +
            " contentId=" +
            String(resolvedContentId) +
            " channelId=" +
            String(channelId)
        );

        // Step 1 — GET schedule: obtain backend's default recordingValues
        // (correct timeAndChannel.dateTime, channelId, user preferences).
        // alwaysShowOverlay=true means a recording options overlay should
        // be shown in future; one-click record proceeds with the values.
        let recordingValues: TRecordingValues;
        try {
          const scheduleResponse =
            selectedRecordingValues !== undefined
              ? {
                  recordingValues: selectedRecordingValues,
                  alwaysShowOverlay: false
                }
              : await service.fetchOfferRecordingSchedule(
                  {
                    offerId: resolvedOfferId,
                    contentId: resolvedContentId,
                    channelId
                  },
                  guardedSignal
                );
          Log.d(
            this.TAG,
            "createOfferRecording() GET schedule: alwaysShowOverlay=" +
              String(scheduleResponse.alwaysShowOverlay)
          );
          const scheduledRecordingValues = (
            scheduleResponse as unknown as TRecordingValuesWithDisplayOptions
          ).recordingValues;
          recordingValues =
            scheduledRecordingValues === undefined ||
            scheduledRecordingValues === null
              ? RecordingsServiceInteractor.buildDefaultRecordingValues(
                  resolvedOfferId,
                  channelId,
                  resolvedAiringStartTime
                )
              : scheduledRecordingValues;
        } catch (scheduleError: unknown) {
          Log.w(
            this.TAG,
            "createOfferRecording() GET schedule failed — using client defaults",
            scheduleError
          );
          recordingValues =
            RecordingsServiceInteractor.buildDefaultRecordingValues(
              resolvedOfferId,
              channelId,
              resolvedAiringStartTime
            );
        }
        // Step 2 — POST schedule with resolved recordingValues.
        Log.d(this.TAG, "createOfferRecording() Step 2: POST schedule");
        // TRecordingsOfferMutationParams: offerId, contentId, channelNumber?,
        // channelId?, xAmznRequestId?, recordingValues.
        // Header fields (applicationName, etc.) are passed to the service
        // constructor via servicesParams, not to the method.
        await service.createOfferRecordingSchedule(
          {
            offerId: resolvedOfferId,
            contentId: resolvedContentId,
            channelNumber,
            channelId,
            xAmznRequestId: servicesParams.xAmznRequestId,
            recordingValues
          },
          guardedSignal
        );
        clearServiceCache(ActionsService, this.TAG);
        Log.i(this.TAG, "createOfferRecording() succeeded");
        AnalyticsLogger.logCreateRecording({
          content_id:
            resolvedContentId === null ? undefined : resolvedContentId,
          channel_id: channelId,
          ...analyticsFields
        });
      },
      options
    );
  }
}
